/**
 * DEMO — placeholder cartridge.
 *
 * Single purpose: prove the multi-cartridge cabinet pipeline works. A
 * pixel rectangle bounces around the screen; arrow keys nudge it; Z
 * triggers a color cycle. Z again from game-over returns to the selector.
 *
 * NOT a real game. NOT a brand cartridge. Throwaway. Replace with a
 * sibling story once we have a second real cartridge built (INTERCEPTOR
 * — sc-2815).
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';

const WIDTH = 480;
const HEIGHT = 270;
const PADDING = 24;

const PLAYER_W = 32;
const PLAYER_H = 32;
const PLAYER_SPEED = 3;

const COLORS: readonly string[] = ['#ffea00', '#00d9ff', '#ff00aa', '#27e060', '#ff3c8a'];

export class DemoInstance implements GameInstance {
  private readonly ctx: GameContext;

  private x = WIDTH / 2 - PLAYER_W / 2;
  private y = HEIGHT / 2 - PLAYER_H / 2;
  private vx = 1;
  private vy = 1;
  private colorIdx = 0;
  private blink = 0;
  private inLeft = false;
  private inRight = false;
  private inUp = false;
  private inDown = false;
  private firePressed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    // Random-ish initial direction via deterministic PRNG so two seeds
    // produce different motion (proves PRNG plumbing reaches DEMO too).
    this.vx = this.ctx.random.next() < 0.5 ? -1 : 1;
    this.vy = this.ctx.random.next() < 0.5 ? -1 : 1;
  }

  input(state: InputState): void {
    this.inLeft = state.left;
    this.inRight = state.right;
    this.inUp = state.up;
    this.inDown = state.down;
    if (state.justPressed.has('fireA')) {
      this.firePressed = true;
    }
  }

  update(_dt: number): void {
    if (this.firePressed) {
      this.colorIdx = (this.colorIdx + 1) % COLORS.length;
      this.firePressed = false;
    }

    // Player nudge.
    if (this.inLeft) this.x -= PLAYER_SPEED;
    if (this.inRight) this.x += PLAYER_SPEED;
    if (this.inUp) this.y -= PLAYER_SPEED;
    if (this.inDown) this.y += PLAYER_SPEED;

    // Auto-drift (so the screen isn't static if nobody plays).
    this.x += this.vx * 0.4;
    this.y += this.vy * 0.4;

    // Bounce off walls.
    if (this.x < PADDING) {
      this.x = PADDING;
      this.vx = Math.abs(this.vx);
    } else if (this.x + PLAYER_W > WIDTH - PADDING) {
      this.x = WIDTH - PADDING - PLAYER_W;
      this.vx = -Math.abs(this.vx);
    }
    if (this.y < PADDING) {
      this.y = PADDING;
      this.vy = Math.abs(this.vy);
    } else if (this.y + PLAYER_H > HEIGHT - PADDING) {
      this.y = HEIGHT - PADDING - PLAYER_H;
      this.vy = -Math.abs(this.vy);
    }

    this.blink = (this.blink + 1) % 60;
  }

  render(g: CanvasRenderingContext2D): void {
    // Background.
    g.fillStyle = '#0a0418';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // Frame.
    g.strokeStyle = '#5a1a78';
    g.lineWidth = 2;
    g.strokeRect(8, 8, WIDTH - 16, HEIGHT - 16);

    // Player rect.
    const c = COLORS[this.colorIdx] ?? COLORS[0]!;
    g.fillStyle = c;
    g.fillRect(this.x, this.y, PLAYER_W, PLAYER_H);

    // Title.
    g.fillStyle = '#ffea00';
    g.font = 'bold 14px ui-monospace, monospace';
    g.textAlign = 'center';
    g.textBaseline = 'top';
    g.fillText('DEMO', WIDTH / 2, 18);

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = '#7af3c8';
    g.fillText('PLACEHOLDER CARTRIDGE', WIDTH / 2, 38);

    // Controls hint.
    g.font = '8px ui-monospace, monospace';
    g.fillStyle = '#00d9ff';
    g.textAlign = 'center';
    g.textBaseline = 'bottom';
    if (this.blink < 45) {
      g.fillText('ARROWS — MOVE  /  Z — COLOR  /  ESC — PAUSE', WIDTH / 2, HEIGHT - 14);
    }

    g.textAlign = 'left';
    g.textBaseline = 'top';
  }

  resize(_w: number, _h: number): void {
    /* 480x270 internal */
  }
  pause(): void {
    /* no audio */
  }
  resume(): void {
    /* no audio */
  }
  teardown(): void {
    /* no allocations */
  }
  getScore(): number {
    return 0;
  }
  getFinalScore(): number {
    return 0;
  }
}
